/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import cn from "classnames";

import { UnGuard } from "constants/interfaces";
import { useTranslation } from "react-i18next";
import { GetTripsResponse } from "lib/tripsApiTypes";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import downloadFile from "utils/downloadFile";

import { HR, Button, Stack, Text, FontAwesomeIcon } from "components";
import { pushToDataLayer } from "utils/gtm";

import FlightCard from "./FlightSectionComponent/FlightCard";

const FlightSection = ({
  flight,
  number,
  trip,
}: {
  number: number;
  trip?: UnGuard<GetTripsResponse["approved_trips"]>[number];
  flight: UnGuard<
    UnGuard<GetTripsResponse["approved_trips"]>[number]["flights"]
  >[number];
}) => {
  const { userData } = useUser();
  const { t } = useTranslation();

  const [isExpanded, setExpanded] = useState(true);

  if (!flight) return null;

  return (
    <Stack className="gap-y-5">
      <div className="flex justify-between w-full items-center -bg--theme-colors-surface-neutral-level03 -rounded-heading-container">
        <Button
          variant="ghost-unstyled"
          className="p-0"
          onClick={() => {
            setExpanded(!isExpanded);
          }}
        >
          <div className="flex gap-x-1.5 items-center">
            <div className="p-2">
              <FontAwesomeIcon
                className={cn(
                  !isExpanded ? "" : "rotate-180",
                  "-text--theme-colors-action-ghost-default-fg grow p-1",
                )}
                name="fa-chevron-down"
                size="xs"
              />
            </div>

            <Text className="-text--theme-colors-text-subtitle-brand font-bold leading-6 text-base">
              {t("flightSection.flightLabel")} {number}
            </Text>
          </div>
        </Button>

        <div className="flex h-full me-2.5 gap-x-2 items-center">
          {!!flight.outbound_flight_pdf?.length && (
            <Button
              variant="ghost-unstyled"
              onClick={() => {
                pushToDataLayer({
                  event: "file_download",
                  screen_name: "dashboard",
                  journey_step: "dashboard",
                  action: "file_outbound_flight_pdf",
                });
                downloadFile(flight.outbound_flight_pdf!);
              }}
              className="px-2 pt-3"
            >
              <div className="-text--theme_colors-icon-neutral">
                {!flight?.is_added_by_user && (
                  <FontAwesomeIcon name="fa-cloud-arrow-down" />
                )}
              </div>
            </Button>
          )}

          {/* {flight.is_added_by_user && ( */}
          <Button
            as="link"
            to={ROUTES.dashboard.flight(userData?.hash_id!, flight.id!)}
            variant="ghost-unstyled"
            size="sm"
          >
            <div className="-text--theme-colors-action-ghost-default-fg">
              <FontAwesomeIcon name="fa-pen" size="xl" className="p-1" />
            </div>
          </Button>
          {/* )} */}
        </div>
      </div>

      {isExpanded && (
        <Stack className="gap-y-5">
          <Text className="md:hidden font-bold">
            {t("flightSection.outboundLabel")}
          </Text>

          <FlightCard type="outbound" trip={trip} flight={flight} />

          {flight.return_departure_date && (
            <>
              <HR className="!my-0 border-dashed md:border-solid" />

              <Text className="md:hidden font-bold">
                {t("flightSection.returnLabel")}
              </Text>

              <FlightCard type="return" trip={trip} flight={flight} />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default FlightSection;
